import { useEffect, useRef } from 'react';

/**
 * Poll `callback` every `intervalMs`, without ever overlapping runs.
 *
 * setInterval fires on a fixed schedule regardless of whether the previous run
 * finished. When an endpoint is slower than the interval, requests pile up
 * without bound until the browser runs out of sockets and every fetch fails
 * with net::ERR_INSUFFICIENT_RESOURCES. This schedules the next run only after
 * the previous one settles, so a slow backend just means a slower poll rate.
 *
 * The callback is held in a ref, so passing an inline arrow function is fine:
 * the timer is not torn down and recreated on every render, and each tick still
 * sees the latest props and state.
 */
export const usePolling = (callback, intervalMs, enabled = true) => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !intervalMs) {
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        await callbackRef.current?.();
      } catch (error) {
        // Callers surface their own errors; swallow here so one failure
        // doesn't stop the polling loop.
        console.error('Polling callback failed:', error);
      } finally {
        if (!cancelled) {
          timer = setTimeout(tick, intervalMs);
        }
      }
    };

    timer = setTimeout(tick, intervalMs);

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [intervalMs, enabled]);
};

export default usePolling;
